import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, LogOut, Camera, CheckCircle2, AlertTriangle, Truck, Fuel, X, Paperclip } from 'lucide-react';
import { api, fetchProtectedFile } from '../services/api';
import senerfiLogo from '../assets/images/Senerfi.gif';
import { getPreopConfigForVehicleType, createPreopChecklistState, countPendingItems } from './modules/preoperacionalDiarioConfig';
import { generatePreoperacionalPdfBlob } from '../Components/print/printPreoperacional';
import SignaturePad from '../Components/Common/SignaturePad';

const TOKEN_KEY = 'senerfi_conductor_token';
// Documentos adicionales opcionales del preoperacional: PDF, JPG o PNG.
// PDF hasta 10 MB (mismo límite que valida conductor_preop.php); las fotos
// se reducen antes de enviarse, así que se acepta un original más pesado.
const DOC_MAX_BYTES = 10 * 1024 * 1024;
const DOC_IMAGE_MAX_BYTES = 25 * 1024 * 1024;
const DOC_DEFS = [
  { type: 'ORDEN_TRABAJO', field: 'ot_document', title: 'Adjuntar orden de trabajo', help: 'Documento suministrado por el coordinador.' },
  { type: 'APTITUD_FISICA', field: 'aptitude_document', title: 'Adjuntar certificado de aptitud física', help: 'Certificado de aptitud física del trabajador.' },
];
const emptyDocMap = (value = null) => Object.fromEntries(DOC_DEFS.map((d) => [d.type, value]));

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(v / 1024))} KB`;
}

function formatDateTime(value) {
  const d = new Date(String(value || '').replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-CO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// Valida tipo y tamaño antes de aceptar el archivo. Devuelve '' si sirve.
function validateDocFile(f) {
  const name = String(f?.name || '').toLowerCase();
  const type = String(f?.type || '').toLowerCase();
  const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
  const isImg = ['image/jpeg', 'image/jpg', 'image/png'].includes(type) || /\.(jpe?g|png)$/.test(name);
  if (!isPdf && !isImg) return 'Formato no permitido. Usa PDF, JPG o PNG.';
  if (isPdf && f.size > DOC_MAX_BYTES) return 'El PDF supera el máximo de 10 MB.';
  if (isImg && f.size > DOC_IMAGE_MAX_BYTES) return 'La imagen es demasiado pesada (máximo 25 MB).';
  return '';
}
const CONDUCTOR_KEY = 'senerfi_conductor_profile';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) { resolve(''); return; }
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Las fotos de cámara (o de galería) de un celular actual pueden pesar
// varios MB cada una — 3 de esas como base64 sin comprimir, más su preview
// en pantalla, congelaban el WebView (no dejaba hacer scroll) y a veces
// hacían fallar el envío. Se reescalan a un máximo razonable en un canvas
// antes de usarlas como preview o de enviarlas, igual que haría cualquier
// app que sube fotos desde el celular.
function compressImageToDataUrl(file, { maxDimension = 1600, quality = 0.75 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) { resolve(''); return; }
    if (!String(file.type || '').startsWith('image/')) {
      fileToDataUrl(file).then(resolve).catch(reject);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > maxDimension || height > maxDimension) {
          if (width >= height) {
            height = Math.round((height / width) * maxDimension);
            width = maxDimension;
          } else {
            width = Math.round((width / height) * maxDimension);
            height = maxDimension;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        URL.revokeObjectURL(objectUrl);
        resolve(dataUrl || '');
      } catch (e) {
        URL.revokeObjectURL(objectUrl);
        fileToDataUrl(file).then(resolve).catch(reject);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      fileToDataUrl(file).then(resolve).catch(reject);
    };
    img.src = objectUrl;
  });
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// Un documento adicional opcional: estado, vista previa, adjuntar,
// reemplazar o quitar. "existing" es lo que ya quedó guardado en el
// preoperacional de hoy (al corregirlo).
function DocumentPicker({ def, file, previewUrl, existing, removeExisting, existingPreview, error, onSelect, onClear, onToggleRemove, onViewExisting }) {
  const inputId = `doc-input-${def.type}`;
  let status;
  if (error) {
    status = <span className="text-red-600">No fue posible cargar el archivo: {error}</span>;
  } else if (file) {
    status = <span className="text-blue-700">Seleccionado: {file.name} ({formatBytes(file.size)}) · se cargará al enviar</span>;
  } else if (existing && removeExisting) {
    status = <span className="text-amber-700">Se quitará al enviar: {existing.name || existing.label}</span>;
  } else if (existing) {
    status = <span className="text-green-700">Carga exitosa: {existing.name || existing.label}{existing.uploaded_at ? ` · ${formatDateTime(existing.uploaded_at)}` : ''}</span>;
  } else {
    status = <span className="text-gray-500">Pendiente de adjuntar (opcional)</span>;
  }
  const hasDoc = !!file || (!!existing && !removeExisting);
  return (
    <div className="border rounded-lg p-3 mt-2">
      <div className="flex items-start gap-2">
        <Paperclip size={16} className="shrink-0 mt-0.5 text-gray-500" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900">{def.title}</div>
          <div className="text-xs text-gray-500">{def.help}</div>
          <div className="text-xs mt-1 break-words">{status}</div>
        </div>
      </div>
      {file && previewUrl && (
        <img src={previewUrl} alt={`Vista previa ${def.title}`} className="mt-2 max-h-40 rounded border object-contain mx-auto" />
      )}
      {!file && existing && !removeExisting && existingPreview && (
        existingPreview.kind === 'image'
          ? <img src={existingPreview.url} alt={`Documento ${def.title}`} className="mt-2 max-h-40 rounded border object-contain mx-auto" />
          : <div className="mt-2 text-xs text-gray-500">{existingPreview.message}</div>
      )}
      <div className="flex flex-wrap gap-2 mt-2">
        <label htmlFor={inputId} className="px-3 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer">
          {hasDoc ? 'Reemplazar' : '+ Adjuntar'}
        </label>
        <input
          id={inputId}
          type="file"
          accept="application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            e.target.value = '';
            if (f) onSelect(f);
          }}
        />
        {file && (
          <button type="button" onClick={onClear} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-red-600">Quitar</button>
        )}
        {!file && existing && !removeExisting && (
          <>
            <button type="button" onClick={onViewExisting} className="px-3 py-1.5 rounded-lg border text-xs font-semibold">Ver</button>
            <button type="button" onClick={onToggleRemove} className="px-3 py-1.5 rounded-lg border text-xs font-semibold text-red-600">Quitar</button>
          </>
        )}
        {!file && existing && removeExisting && (
          <button type="button" onClick={onToggleRemove} className="px-3 py-1.5 rounded-lg border text-xs font-semibold">Deshacer</button>
        )}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, loading, error }) {
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col items-center mb-6">
          <img src={senerfiLogo} alt="Senerfi" className="h-14 object-contain mb-2" />
          <h1 className="text-lg font-semibold text-gray-900">Preoperacionales</h1>
          <p className="text-sm text-gray-500 text-center">Ingresa con tu usuario de Conductor</p>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); onLogin(usuario.trim(), password); }}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
            <input
              type="text"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              autoCapitalize="none"
              autoCorrect="off"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          {error && (
            <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{error}</div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
            style={{ backgroundColor: '#16A34A' }}
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
        <p className="mt-4 text-xs text-gray-400 text-center">
          ¿No tienes usuario? Pídelo a tu coordinador de Transportes (módulo Conductores).
        </p>
      </div>
    </div>
  );
}

function OptionPills({ value, options, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map((opt) => {
        const active = value === opt;
        const isNegative = /malo|mala|no |no$|incorrecta|incompleto|incompleta|sucio|anormal|no tiene|vac[ií]o/i.test(opt) && !/no aplica/i.test(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              active
                ? (isNegative ? 'bg-red-600 border-red-600 text-white' : 'bg-green-600 border-green-600 text-white')
                : 'bg-white border-gray-300 text-gray-600'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default function ConductorPreoperacional() {
  const [token, setToken] = useState(() => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (_) { return ''; } });
  const [conductor, setConductor] = useState(() => { try { return JSON.parse(localStorage.getItem(CONDUCTOR_KEY) || 'null'); } catch (_) { return null; } });
  const [booting, setBooting] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState('');

  const config = useMemo(() => getPreopConfigForVehicleType(vehicle?.vehicle_type), [vehicle]);

  const [todayRecord, setTodayRecord] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [sections, setSections] = useState([]);
  const [mileage, setMileage] = useState('');
  const [projectResponsible, setProjectResponsible] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [licenseCategory, setLicenseCategory] = useState('');
  const [extintorFecha, setExtintorFecha] = useState('');
  const [hasFindings, setHasFindings] = useState('No');
  const [observations, setObservations] = useState('');
  const [evidencePhotoFile, setEvidencePhotoFile] = useState(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  // Documentos adicionales (orden de trabajo, aptitud física), por tipo.
  const [docFiles, setDocFiles] = useState(emptyDocMap());
  const [docPreviews, setDocPreviews] = useState(emptyDocMap(''));
  const [docErrors, setDocErrors] = useState(emptyDocMap(''));
  const [docRemove, setDocRemove] = useState(emptyDocMap(false));
  const [docExistingPreview, setDocExistingPreview] = useState(emptyDocMap());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [result, setResult] = useState(null);

  const emptyTanqueoForm = () => ({
    conductor: conductor?.nombre || '',
    centroCosto: '', municipio: '', kmHorometro: '', numeroComprobante: '',
    valor: '', tipoCombustible: '', galones: '', eds: '', tipoTanqueo: '',
    fotoKm: null, fotoTicket: null, fotoDispensador: null,
  });
  const [tanqueoOpen, setTanqueoOpen] = useState(false);
  const [tanqueoSaving, setTanqueoSaving] = useState(false);
  const [tanqueoError, setTanqueoError] = useState('');
  const [tanqueoForm, setTanqueoForm] = useState(emptyTanqueoForm());
  const [tanqueoPreviews, setTanqueoPreviews] = useState({ fotoKm: null, fotoTicket: null, fotoDispensador: null });

  useEffect(() => {
    if (!vehicle) return;
    setSections(createPreopChecklistState(vehicle.vehicle_type));
  }, [vehicle]);

  const persistSession = (tok, cond) => {
    try {
      if (tok) localStorage.setItem(TOKEN_KEY, tok); else localStorage.removeItem(TOKEN_KEY);
      if (cond) localStorage.setItem(CONDUCTOR_KEY, JSON.stringify(cond)); else localStorage.removeItem(CONDUCTOR_KEY);
    } catch (_) {}
  };

  const loadVehicles = useCallback(async (tok) => {
    setVehiclesLoading(true);
    setVehiclesError('');
    try {
      const res = await api.conductorPreop.myVehicle(tok);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setVehicles(rows);
      if (rows.length === 1) setVehicle(rows[0]);
    } catch (e) {
      setVehiclesError(e?.message || 'No fue posible cargar tu vehículo asignado.');
    } finally {
      setVehiclesLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (!token) { setBooting(false); return; }
      try {
        const res = await api.conductorAuth.me(token);
        if (!res?.ok) throw new Error('Sesión inválida');
        setConductor(res.conductor);
        persistSession(token, res.conductor);
        await loadVehicles(token);
      } catch (_) {
        setToken('');
        setConductor(null);
        persistSession('', null);
      } finally {
        setBooting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!vehicle || !token) return;
    (async () => {
      try {
        const res = await api.conductorPreop.today(token, vehicle.id);
        if (res?.ok && res.found) setTodayRecord(res.data);
        else setTodayRecord(null);
      } catch (_) { setTodayRecord(null); }
    })();
  }, [vehicle, token]);

  const handleLogin = async (usuario, password) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await api.conductorAuth.login(usuario, password);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible ingresar.');
      setToken(res.token);
      setConductor(res.conductor);
      persistSession(res.token, res.conductor);
      await loadVehicles(res.token);
    } catch (e) {
      setLoginError(e?.message || 'Usuario o contraseña incorrectos.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await api.conductorAuth.logout(token); } catch (_) {}
    setToken('');
    setConductor(null);
    setVehicle(null);
    setVehicles([]);
    setResult(null);
    setShowForm(false);
    persistSession('', null);
  };

  const setItemValue = (sectionKey, itemKey, value) => {
    setSections((prev) => prev.map((sec) => (
      sec.key !== sectionKey ? sec : {
        ...sec,
        items: sec.items.map((it) => (it.key === itemKey ? { ...it, value } : it)),
      }
    )));
  };

  const pendingCount = useMemo(() => countPendingItems(sections), [sections]);

  const resetForm = () => {
    setSections(createPreopChecklistState(vehicle?.vehicle_type));
    setMileage(''); setProjectResponsible('');
    setLicenseNumber(''); setLicenseExpiry(''); setLicenseCategory('');
    setExtintorFecha(''); setHasFindings('No'); setObservations(''); setEvidencePhotoFile(null);
    setSignatureDataUrl(''); setPhotoFile(null);
    clearDocState();
    setSubmitError(''); setResult(null); setShowForm(false);
  };

  // ---- Documentos adicionales (orden de trabajo, aptitud física)
  const existingDocs = useMemo(() => {
    const map = emptyDocMap();
    (Array.isArray(todayRecord?.documents) ? todayRecord.documents : []).forEach((d) => {
      if (d && d.type in map) map[d.type] = d;
    });
    return map;
  }, [todayRecord]);

  const revokePreview = (url) => { if (url && url.startsWith('blob:')) { try { URL.revokeObjectURL(url); } catch (_) {} } };

  function clearDocState() {
    Object.values(docPreviews).forEach(revokePreview);
    Object.values(docExistingPreview).forEach((p) => revokePreview(p?.url));
    setDocFiles(emptyDocMap()); setDocPreviews(emptyDocMap('')); setDocErrors(emptyDocMap(''));
    setDocRemove(emptyDocMap(false)); setDocExistingPreview(emptyDocMap());
  }

  const selectDoc = (type, f) => {
    const err = validateDocFile(f);
    if (err) { setDocErrors((m) => ({ ...m, [type]: err })); return; }
    revokePreview(docPreviews[type]);
    const isImg = /^image\//i.test(f.type || '') || /\.(jpe?g|png)$/i.test(f.name || '');
    setDocFiles((m) => ({ ...m, [type]: f }));
    setDocPreviews((m) => ({ ...m, [type]: isImg ? URL.createObjectURL(f) : '' }));
    setDocErrors((m) => ({ ...m, [type]: '' }));
    setDocRemove((m) => ({ ...m, [type]: false }));
  };

  const clearDoc = (type) => {
    revokePreview(docPreviews[type]);
    setDocFiles((m) => ({ ...m, [type]: null }));
    setDocPreviews((m) => ({ ...m, [type]: '' }));
    setDocErrors((m) => ({ ...m, [type]: '' }));
  };

  const viewExistingDoc = async (type) => {
    const doc = existingDocs[type];
    if (!doc) return;
    if (!/^image\//i.test(doc.mime || '')) {
      setDocExistingPreview((m) => ({ ...m, [type]: { kind: 'message', message: 'Es un PDF: se puede consultar completo en NOVA web (Transportes > Preoperacionales).' } }));
      return;
    }
    try {
      const blob = await fetchProtectedFile(api.conductorPreop.documentUrl(doc.id), { token });
      setDocExistingPreview((m) => ({ ...m, [type]: { kind: 'image', url: URL.createObjectURL(blob) } }));
    } catch (e) {
      setDocExistingPreview((m) => ({ ...m, [type]: { kind: 'message', message: e?.message || 'No fue posible abrir el documento.' } }));
    }
  };

  const submit = async () => {
    setSubmitError('');
    if (!vehicle) { setSubmitError('Selecciona un vehículo.'); return; }
    if (pendingCount > 0) { setSubmitError(`Faltan ${pendingCount} ítem(s) del checklist por responder.`); return; }
    if (!signatureDataUrl) { setSubmitError('La firma del conductor es obligatoria.'); return; }
    if (hasFindings === 'Si' && !observations.trim()) { setSubmitError('Describe el hallazgo en observaciones.'); return; }
    setSubmitting(true);
    try {
      const photoDataUrl = photoFile ? await compressImageToDataUrl(photoFile) : '';
      const evidencePhotoDataUrl = evidencePhotoFile ? await compressImageToDataUrl(evidencePhotoFile) : '';
      // Documentos adicionales: las fotos se reducen igual que las demás;
      // los PDF se envían tal cual. Sin archivo, *_remove=1 quita el que ya
      // estaba adjunto (solo si el conductor pulsó "Quitar").
      const docPayload = {};
      const docDataUrls = {};
      for (const def of DOC_DEFS) {
        const f = docFiles[def.type];
        if (f) {
          const dataUrl = await compressImageToDataUrl(f);
          docDataUrls[def.type] = dataUrl;
          docPayload[`${def.field}_base64`] = dataUrl;
          docPayload[`${def.field}_name`] = f.name || '';
        } else if (existingDocs[def.type] && docRemove[def.type]) {
          docPayload[`${def.field}_remove`] = '1';
        }
      }

      const sectionsPayload = sections.map((sec) => {
        const cfg = config.sections.find((s) => s.key === sec.key);
        return {
          key: sec.key,
          title: sec.title,
          items: sec.items,
          ...(cfg?.dateField ? { date_field: cfg.dateField.key, date_value: extintorFecha } : {}),
        };
      });

      const submitRes = await api.conductorPreop.submit(token, {
        vehicle_id: vehicle.id,
        format_code: config.formatCode,
        mileage_label: config.mileageLabel,
        mileage,
        project_responsible: projectResponsible,
        driver_license_number: licenseNumber,
        driver_license_expiry: licenseExpiry,
        driver_license_category: licenseCategory,
        sections: JSON.stringify(sectionsPayload),
        has_findings: hasFindings,
        observations,
        signature_base64: signatureDataUrl,
        photo_base64: photoDataUrl,
        evidence_photo_base64: evidencePhotoDataUrl,
        ...docPayload,
        // Los 3 responsables ya no firman en este dispositivo: cada uno
        // firma después desde su propia cuenta NOVA (Transportes >
        // Preoperacionales > Pendientes de mi firma). Aquí solo se dejan
        // sus nombres por defecto para que el registro quede identificado.
        auth_signatures: JSON.stringify(config.authRoles.map((role) => ({
          key: role.key, role: role.role, name: role.name,
        }))),
      });
      if (!submitRes?.ok) throw new Error(submitRes?.error || 'No fue posible enviar el preoperacional.');

      try {
        const pdfSections = sections.map((sec) => {
          const cfg = config.sections.find((s) => s.key === sec.key);
          return {
            title: sec.title,
            items: sec.items,
            ...(cfg?.dateField ? { dateValue: extintorFecha, dateFieldLabel: cfg.dateField.label } : {}),
          };
        });
        // Aún sin firma: los responsables firman después desde su cuenta
        // NOVA, y en ese momento se regenera el PDF con su firma real.
        const authForPdf = config.authRoles.map((role) => ({
          role: role.role,
          name: role.name,
          signatureUrl: '',
          signatureDataUrl: '',
        }));
        const { blob } = await generatePreoperacionalPdfBlob({
          formatCode: config.formatCode,
          formatLabel: config.formatLabel,
          mileageLabel: config.mileageLabel,
          vehicle: {
            plate: vehicle.plate, brand: vehicle.brand, model: vehicle.model,
            hasDocuments: vehicle.has_documents, soatExpiry: vehicle.soat_expiry,
            tecnomecanicaExpiry: vehicle.tecnomecanica_expiry, policyExpiry: vehicle.policy_expiry,
          },
          driver: {
            nombre: conductor?.nombre, cedula: conductor?.cedula,
            licenseNumber, licenseExpiry, licenseCategory,
          },
          sections: pdfSections,
          date: submitRes.date,
          mileage,
          projectResponsible,
          hasFindings,
          observations,
          consecutivo: submitRes.consecutivo,
          signatureUrl: submitRes.signature_url,
          signatureDataUrl,
          photoUrl: submitRes.photo_url,
          photoDataUrl,
          evidencePhotoUrl: submitRes.evidence_photo_url,
          evidencePhotoDataUrl,
          documents: (Array.isArray(submitRes.documents) ? submitRes.documents : []).map((d) => ({
            ...d,
            url: api.conductorPreop.documentUrl(d.id),
            token,
            dataUrl: docDataUrls[d.type] || '',
          })),
          authSignatures: authForPdf,
        });
        const pdfDataUrl = await blobToDataUrl(blob);
        await api.conductorPreop.uploadPdf(token, { preop_id: submitRes.id, pdf_base64: pdfDataUrl });
      } catch (pdfErr) {
        // El registro ya quedó guardado aunque falle la generación del PDF;
        // se puede reintentar la descarga desde el panel de reportes.
        console.error('Error generando/subiendo PDF de preoperacional', pdfErr);
      }

      setResult(submitRes);
      setShowForm(false);
      clearDocState();
      // Refresca el registro de hoy para que una corrección muestre los
      // documentos que acaban de quedar guardados.
      try {
        const todayRes = await api.conductorPreop.today(token, vehicle.id);
        setTodayRecord(todayRes?.ok && todayRes.found ? todayRes.data : null);
      } catch (_) {}
    } catch (e) {
      setSubmitError(e?.message || 'No fue posible enviar el preoperacional.');
    } finally {
      setSubmitting(false);
    }
  };

  const openTanqueoModal = () => {
    setTanqueoForm(emptyTanqueoForm());
    setTanqueoPreviews({ fotoKm: null, fotoTicket: null, fotoDispensador: null });
    setTanqueoError('');
    setTanqueoOpen(true);
  };

  const handleTanqueoPhotoChange = async (fieldId, file) => {
    setTanqueoForm((prev) => ({ ...prev, [fieldId]: file || null }));
    if (!file) {
      setTanqueoPreviews((prev) => ({ ...prev, [fieldId]: null }));
      return;
    }
    // Se comprime una sola vez acá y se reutiliza el mismo resultado al
    // enviar (submitTanqueo), en vez de volver a leer el archivo original
    // sin comprimir.
    const dataUrl = await compressImageToDataUrl(file);
    setTanqueoPreviews((prev) => ({ ...prev, [fieldId]: dataUrl }));
  };

  const submitTanqueo = async () => {
    setTanqueoError('');
    if (!vehicle) { setTanqueoError('Selecciona un vehículo.'); return; }
    setTanqueoSaving(true);
    try {
      const fotoKmDataUrl = tanqueoPreviews.fotoKm || '';
      const fotoTicketDataUrl = tanqueoPreviews.fotoTicket || '';
      const fotoDispensadorDataUrl = tanqueoPreviews.fotoDispensador || '';
      const res = await api.conductorPreop.submitTanqueo(token, {
        vehicle_id: vehicle.id,
        conductor: tanqueoForm.conductor,
        centro_costo: tanqueoForm.centroCosto,
        municipio: tanqueoForm.municipio,
        km_horometro: tanqueoForm.kmHorometro,
        numero_comprobante: tanqueoForm.numeroComprobante,
        valor: tanqueoForm.valor,
        tipo_combustible: tanqueoForm.tipoCombustible,
        galones: tanqueoForm.galones,
        eds: tanqueoForm.eds,
        tipo_tanqueo: tanqueoForm.tipoTanqueo,
        foto_km_base64: fotoKmDataUrl,
        foto_ticket_base64: fotoTicketDataUrl,
        foto_dispensador_base64: fotoDispensadorDataUrl,
      });
      if (!res?.ok) throw new Error(res?.error || 'No fue posible registrar el tanqueo.');
      setTanqueoOpen(false);
      alert('Tanqueo registrado correctamente.');
    } catch (e) {
      setTanqueoError(e?.message || 'No fue posible registrar el tanqueo.');
    } finally {
      setTanqueoSaving(false);
    }
  };

  const renderTanqueoModal = () => {
    if (!tanqueoOpen) return null;
    const combustibles = ['Gasolina', 'ACPM'];
    const edsOptions = ['TERPEL', 'BIOMAX', 'PRIMAX', 'TEXACO', 'PETROMIL'];
    const tipoTanqueoOptions = ['Parcial', 'Lleno'];
    const photoFields = [
      { id: 'fotoKm', label: 'Foto del kilometraje/horómetro' },
      { id: 'fotoTicket', label: 'Foto del ticket/comprobante' },
      { id: 'fotoDispensador', label: 'Foto del dispensador' },
    ];
    // Un solo contenedor con scroll (el fixed inset-0 exterior), sin
    // overflow-y-auto anidado ni max-h-[90vh]: ese combo es justo el que
    // falla en algunos WebView de Android (cálculo de vh poco confiable con
    // la barra de estado/gestos, y el scroll dentro de un hijo con overflow
    // anidado dentro de un padre fixed a veces no captura el gesto táctil)
    // — con un solo scroll en el contenedor raíz, el botón de guardar
    // siempre es alcanzable sin importar cuánto contenido (fotos) haya
    // cargado.
    return (
      <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-auto my-6">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <Fuel className="text-orange-600" size={20} />
              <p className="font-bold text-gray-800">Registrar tanqueo · {vehicle?.plate}</p>
            </div>
            <button type="button" onClick={() => setTanqueoOpen(false)} className="p-1 text-gray-500 hover:bg-gray-100 rounded-lg">
              <X size={18} />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Conductor" value={tanqueoForm.conductor} onChange={(e) => setTanqueoForm((p) => ({ ...p, conductor: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm col-span-2" />
              <input type="text" placeholder="Centro de costo" value={tanqueoForm.centroCosto} onChange={(e) => setTanqueoForm((p) => ({ ...p, centroCosto: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Municipio" value={tanqueoForm.municipio} onChange={(e) => setTanqueoForm((p) => ({ ...p, municipio: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Km / horómetro" value={tanqueoForm.kmHorometro} onChange={(e) => setTanqueoForm((p) => ({ ...p, kmHorometro: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Nº comprobante" value={tanqueoForm.numeroComprobante} onChange={(e) => setTanqueoForm((p) => ({ ...p, numeroComprobante: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Valor" value={tanqueoForm.valor} onChange={(e) => setTanqueoForm((p) => ({ ...p, valor: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <input type="text" placeholder="Galones" value={tanqueoForm.galones} onChange={(e) => setTanqueoForm((p) => ({ ...p, galones: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" />
              <select value={tanqueoForm.tipoCombustible} onChange={(e) => setTanqueoForm((p) => ({ ...p, tipoCombustible: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm">
                <option value="">Tipo de combustible</option>
                {combustibles.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <select value={tanqueoForm.eds} onChange={(e) => setTanqueoForm((p) => ({ ...p, eds: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm">
                <option value="">Estación de servicio</option>
                {edsOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
              <select value={tanqueoForm.tipoTanqueo} onChange={(e) => setTanqueoForm((p) => ({ ...p, tipoTanqueo: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm col-span-2">
                <option value="">Tipo de tanqueo</option>
                {tipoTanqueoOptions.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {photoFields.map((pf) => (
                <div key={pf.id}>
                  <label className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer">
                    <Camera size={16} />
                    {tanqueoForm[pf.id] ? tanqueoForm[pf.id].name : pf.label}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleTanqueoPhotoChange(pf.id, e.target.files?.[0] || null)} />
                  </label>
                  {tanqueoPreviews[pf.id] && (
                    <img src={tanqueoPreviews[pf.id]} alt={pf.label} className="w-full h-24 object-cover rounded-lg border mt-1" />
                  )}
                </div>
              ))}
            </div>
            {tanqueoError && (
              <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{tanqueoError}</div>
            )}
            <button
              type="button"
              disabled={tanqueoSaving}
              onClick={submitTanqueo}
              className="w-full py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-60"
              style={{ backgroundColor: '#EA580C' }}
            >
              {tanqueoSaving ? 'Guardando...' : 'Guardar tanqueo'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (booting) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">Cargando...</div>;
  }

  if (!token || !conductor) {
    return <LoginScreen onLogin={handleLogin} loading={loginLoading} error={loginError} />;
  }

  const HeaderBar = (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
      <div className="flex items-center gap-2">
        <img src={senerfiLogo} alt="Senerfi" className="h-8 object-contain" />
        <div>
          <div className="text-sm font-semibold text-gray-900">{conductor.nombre}</div>
          <div className="text-xs text-gray-500">Preoperacional diario</div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {vehicle && (
          <button type="button" onClick={openTanqueoModal} className="p-2 rounded-lg text-orange-600 hover:bg-orange-50" title="Registrar tanqueo">
            <Fuel size={18} />
          </button>
        )}
        <button type="button" onClick={handleLogout} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100" title="Salir">
          <LogOut size={18} />
        </button>
      </div>
    </div>
  );
  const HeaderBarWithModal = (
    <>
      {HeaderBar}
      {renderTanqueoModal()}
    </>
  );

  if (vehiclesLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {HeaderBarWithModal}
        <div className="p-6 text-center text-gray-500 text-sm">Buscando tu vehículo asignado...</div>
      </div>
    );
  }

  if (vehiclesError || vehicles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {HeaderBarWithModal}
        <div className="p-6 max-w-md mx-auto text-center">
          <AlertTriangle className="mx-auto text-amber-500 mb-2" size={32} />
          <p className="text-sm text-gray-600">
            {vehiclesError || 'No tienes un vehículo asignado activo. Contacta a tu coordinador de Transportes.'}
          </p>
          <button type="button" onClick={() => loadVehicles(token)} className="mt-4 px-4 py-2 rounded-lg border text-sm">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50">
        {HeaderBarWithModal}
        <div className="p-4">
          <p className="text-sm text-gray-600 mb-3">Selecciona el vehículo:</p>
          <div className="space-y-2">
            {vehicles.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setVehicle(v)}
                className="w-full flex items-center gap-3 p-3 bg-white border rounded-xl text-left hover:bg-gray-50"
              >
                <Truck className="text-blue-600" size={20} />
                <div>
                  <div className="font-semibold text-sm">{v.plate}</div>
                  <div className="text-xs text-gray-500">{[v.brand, v.model].filter(Boolean).join(' ')} {v.vehicle_type ? `· ${v.vehicle_type}` : ''}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-gray-50">
        {HeaderBarWithModal}
        <div className="p-6 max-w-md mx-auto text-center">
          <CheckCircle2 className="mx-auto text-green-600 mb-2" size={40} />
          <h2 className="text-lg font-semibold text-gray-900">Preoperacional enviado</h2>
          <p className="text-sm text-gray-500 mt-1">
            Placa {result.plate} · Consecutivo {result.consecutivo} · Estado: <b>{result.status}</b>
          </p>
          {Array.isArray(result.documents) && result.documents.length > 0 && (
            <div className="mt-3 text-sm text-green-700">
              {result.documents.map((d) => <div key={d.id}>Carga exitosa: {d.label}</div>)}
            </div>
          )}
          {Array.isArray(result.document_errors) && result.document_errors.length > 0 && (
            <div className="mt-3 px-3 py-2 rounded-lg border text-sm bg-amber-50 border-amber-200 text-amber-800 text-left">
              {result.document_errors.map((m) => <div key={m}>{m}</div>)}
            </div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <button type="button" onClick={resetForm} className="w-full py-2.5 rounded-lg border text-sm font-semibold">
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (todayRecord && !showForm) {
    return (
      <div className="min-h-screen bg-gray-50">
        {HeaderBarWithModal}
        <div className="p-6 max-w-md mx-auto text-center">
          <CheckCircle2 className="mx-auto text-green-600 mb-2" size={36} />
          <h2 className="text-base font-semibold text-gray-900">Ya diligenciaste el preoperacional de hoy</h2>
          <p className="text-sm text-gray-500 mt-1">
            Placa {vehicle.plate} · Estado: <b>{todayRecord.status}</b>
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button type="button" onClick={() => setShowForm(true)} className="w-full py-2.5 rounded-lg border text-sm font-semibold">
              Diligenciar de nuevo (corregir)
            </button>
            <button type="button" onClick={() => setVehicle(null)} className="w-full py-2.5 rounded-lg text-gray-500 text-sm">
              Cambiar vehículo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {HeaderBarWithModal}
      <div className="p-4 space-y-4 max-w-xl md:max-w-4xl lg:max-w-5xl mx-auto">
        <div className="bg-white border rounded-xl p-3 flex items-center gap-3">
          <Truck className="text-blue-600" size={22} />
          <div>
            <div className="font-semibold text-sm">{vehicle.plate} · {config.formatLabel}</div>
            <div className="text-xs text-gray-500">{[vehicle.brand, vehicle.model].filter(Boolean).join(' ')}</div>
          </div>
          <button type="button" onClick={() => setVehicle(null)} className="ml-auto text-xs text-blue-600">Cambiar</button>
        </div>

        <div className="bg-white border rounded-xl p-3">
          <div className="text-sm font-semibold text-gray-900 mb-2">Datos del conductor y {config.mileageLabel.toLowerCase()}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input type="text" placeholder="Licencia de conducción" value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Vencimiento licencia</label>
                <input type="date" value={licenseExpiry} onChange={(e) => setLicenseExpiry(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <input type="text" placeholder="Categoría (ej. C1)" value={licenseCategory} onChange={(e) => setLicenseCategory(e.target.value)} className="border rounded-lg px-3 py-2 text-sm self-end" />
            </div>
            <input type="number" min="0" max="9999999" placeholder={`${config.mileageLabel} inicial`} value={mileage} onChange={(e) => setMileage(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
            <input type="text" placeholder="Proyecto / Responsable" value={projectResponsible} onChange={(e) => setProjectResponsible(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sections.map((sec) => {
            const cfg = config.sections.find((s) => s.key === sec.key);
            return (
              <div key={sec.key} className="bg-white border rounded-xl p-3">
                <div className="text-sm font-semibold text-gray-900 mb-2">{sec.title}</div>
                <div className="space-y-3">
                  {sec.items.map((it) => {
                    const optCfg = cfg?.items?.find((c) => c.key === it.key);
                    return (
                      <div key={it.key}>
                        <div className="text-xs text-gray-600">{it.label}</div>
                        <OptionPills value={it.value} options={optCfg?.options || []} onChange={(v) => setItemValue(sec.key, it.key, v)} />
                      </div>
                    );
                  })}
                  {cfg?.dateField && (
                    <div>
                      <label className="text-xs text-gray-500">{cfg.dateField.label}</label>
                      <input type="date" value={extintorFecha} onChange={(e) => setExtintorFecha(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white border rounded-xl p-3">
            <div className="text-sm font-semibold text-gray-900 mb-2">Observaciones</div>
            <div className="text-xs text-gray-600 mb-1">¿Hay hallazgos o seguimiento?</div>
            <OptionPills value={hasFindings} options={['Si', 'No']} onChange={setHasFindings} />
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Observaciones generales"
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm mt-2"
            />
            <label className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer mt-2">
              <Camera size={16} />
              {evidencePhotoFile ? evidencePhotoFile.name : 'Foto del hallazgo / del vehículo (opcional)'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setEvidencePhotoFile(e.target.files?.[0] || null)} />
            </label>
          </div>

          <div className="bg-white border rounded-xl p-3">
            <div className="text-sm font-semibold text-gray-900 mb-2">Firma y foto del conductor</div>
            <div className="text-xs text-gray-600 mb-1">Firma (dibújala con el dedo, obligatorio)</div>
            <SignaturePad onChange={setSignatureDataUrl} />
            <label className="flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer mt-3">
              <Camera size={16} />
              {photoFile ? photoFile.name : 'Tomar/adjuntar foto del conductor (opcional)'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-3">
          <div className="text-sm font-semibold text-gray-900">Documentos adicionales (opcionales)</div>
          <p className="text-xs text-gray-500">PDF, JPG o PNG. No son obligatorios para enviar el preoperacional.</p>
          {DOC_DEFS.map((def) => (
            <DocumentPicker
              key={def.type}
              def={def}
              file={docFiles[def.type]}
              previewUrl={docPreviews[def.type]}
              existing={existingDocs[def.type]}
              removeExisting={docRemove[def.type]}
              existingPreview={docExistingPreview[def.type]}
              error={docErrors[def.type]}
              onSelect={(f) => selectDoc(def.type, f)}
              onClear={() => clearDoc(def.type)}
              onToggleRemove={() => setDocRemove((m) => ({ ...m, [def.type]: !m[def.type] }))}
              onViewExisting={() => viewExistingDoc(def.type)}
            />
          ))}
        </div>

        <div className="bg-white border rounded-xl p-3">
          <div className="text-sm font-semibold text-gray-900 mb-1">Autorización del vehículo</div>
          <p className="text-xs text-gray-500">
            Después de enviar, este preoperacional queda pendiente de la firma de {config.authRoles.map((r) => r.role).join(', ')} — cada uno firma desde su propia cuenta NOVA. Si el preoperacional queda No Apto, el vehículo no podrá salir por portería hasta que el Coordinador SST lo revise.
          </p>
        </div>

        {submitError && (
          <div className="px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">{submitError}</div>
        )}

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3">
          <div className="max-w-xl md:max-w-4xl lg:max-w-5xl mx-auto">
            {pendingCount > 0 && (
              <div className="text-xs text-amber-600 mb-2 text-center">Faltan {pendingCount} ítem(s) por responder</div>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              className="w-full py-3 rounded-lg text-white text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#16A34A' }}
            >
              <ClipboardCheck size={18} />
              {submitting ? 'Enviando...' : 'Enviar preoperacional'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
